import { FuelType } from '@/types/price-comparison.types';
import { ApiResponse } from '@/types/services';
import { RegionalStationsData } from '@/types/regional-stations.types';
import { PerformanceStatus, ProvinceTableResponse } from '@/types/pricing-province.types';

import { baseApi } from './base-api';
import { apiService, downloadBlob } from './api';
import { BrandItem, GetStationsResponse, GetSummaryResponse, SummaryResult } from './pricing-regional.api';

export interface ProvinceStationOptions {
  province: string;
  sortBy?: 'alphabetical' | 'bindings';
  brandFilter?: string;
  includeDiscounts?: boolean;
}
// same as in regional?
export type GetFiltersResponse = ApiResponse<{
  brands: BrandItem[];
  fuelTypes: string[];
  regions: string[];
}>;

export type GetProvinceResponse = ApiResponse<{
  data: ProvinceTableResponse[];
  fuel_types?: FuelType[];
  performanceStatus?: PerformanceStatus;
}>;

export type ProvinceResult = {
  provinceData: ProvinceTableResponse[];
  provinceFuelTypes?: FuelType[];
  provincePerformanceStatus?: PerformanceStatus;
};

export type ProvinceStationsResult = {
  stationsData: RegionalStationsData;
  brands: BrandItem[];
  stationsFuelTypes?: FuelType[];
  performanceStatus?: PerformanceStatus;
};

export const pricingProvinceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getFilters: builder.query<GetFiltersResponse['data'], void>({
      query: () => '/pricing-province/filters',
      providesTags: ['BrandOrder'],
      transformResponse: (response: GetFiltersResponse) => {
        return response.data || {};
      }
    }),

    getGlobalSummary: builder.query<SummaryResult, { includeDiscounts?: boolean }>({
      query: includeDiscounts => ({
        url: '/pricing-province/summary',
        params: includeDiscounts
      }),
      providesTags: ['ProvinceOverallSummary'],
      transformResponse: (response: GetSummaryResponse): SummaryResult => {
        return {
          summaryData: response.data.data || [],
          summaryFuelTypes: response.data.fuel_types || [],
          performanceStatus: response.data.performanceStatus
        };
      }
    }),
    getProvinceData: builder.query<ProvinceResult, { includeDiscounts?: boolean }>({
      query: includeDiscounts => ({
        url: '/pricing-province/provinces',
        params: includeDiscounts
      }),
      providesTags: ['ProvinceSummary'],
      transformResponse: (response: GetProvinceResponse): ProvinceResult => {
        return {
          provinceData: response.data.data || [],
          provinceFuelTypes: response.data.fuel_types || [],
          provincePerformanceStatus: response.data.performanceStatus
        };
      }
    }),

    getProvinceStationsData: builder.query<ProvinceStationsResult, ProvinceStationOptions>({
      query: ({ province, brandFilter, includeDiscounts = true, ...options }) => ({
        url: `/pricing-province/province/${province}`,
        params: {
          includeDiscounts,
          ...options,
          ...(brandFilter ? { brandFilter } : {})
        }
      }),
      transformResponse: (response: GetStationsResponse): ProvinceStationsResult => {
        return {
          stationsData: response.data.data,
          stationsFuelTypes: response.data.fuel_types || [],
          brands: response.data.brands || [],
          performanceStatus: response.data.performanceStatus
        };
      }
    }),

    exportSummaryData: builder.mutation<boolean, boolean | undefined>({
      async queryFn(includeDiscounts = true) {
        try {
          const { blob, filename } = await apiService.getBlob(
            `/pricing-province/export/summary?includeDiscounts=${includeDiscounts}`
          );

          downloadBlob(blob, filename);

          return { data: true };
        } catch (error) {
          return { error: error as any };
        }
      }
    }),

    exportProvinceData: builder.mutation<boolean, { province: string }>({
      async queryFn({ province }) {
        try {
          const { blob, filename } = await apiService.getBlob(`/pricing-province/export/provinces/${province}`);

          downloadBlob(blob, filename);

          return { data: true };
        } catch (error) {
          return { error: error as any };
        }
      }
    })
  })
});
export const {
  useGetFiltersQuery,
  useGetGlobalSummaryQuery,
  useGetProvinceDataQuery,
  useGetProvinceStationsDataQuery,
  useExportSummaryDataMutation,
  useExportProvinceDataMutation
} = pricingProvinceApi;
